import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveData } from 'services/api/saveData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import WarningModal from 'components/modals/WarningModal';
import { clearModuleFeaturesCache } from '../billing/functions/useOrderableModuleFeatures';

interface DeactivateIntegrationButtonProps {
    featureKey: string;
    onDeactivated?: () => void;
};

// Button which deactivates a paid integration after a confirmation
const DeactivateIntegrationButton: React.FC<DeactivateIntegrationButtonProps> = ({ featureKey, onDeactivated }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { environmentHash } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const [deactivating, setDeactivating] = useState(false);

    // Deactivate the integration feature on the subscription
    const handleDeactivate = async () => {
        setDeactivating(true);

        try {
            // Deactivate the module in the backend, this also disconnects the integration
            await saveData({
                apiUrl: `deactivate_module/${environmentHash}`,
                method: 'post',
                data: { module: featureKey }
            });

            // Clear the module features cache so the order state refreshes
            clearModuleFeaturesCache();

            // Show a success alert
            setFloatingAlert({
                type: 'success',
                message: t('integrations.general.deactivate_integration_success')
            });

            // Call the on deactivated callback
            onDeactivated?.();
        } catch (error) {
            console.error('Error deactivating integration', error);
            setFloatingAlert({
                type: 'danger',
                message: t('integrations.general.deactivate_integration_error')
            });
        } finally {
            setDeactivating(false);
        }
    };

    // Open the confirmation modal before deactivating
    const openConfirmationModal = () => {
        initializeModal(
            <WarningModal
                header='integrations.general.deactivate_integration_confirm_title'
                message='integrations.general.deactivate_integration_confirm_message'
                buttonLabel='integrations.general.deactivate_integration_label'
                onApproval={handleDeactivate}
            />,
            { modalSize: 'extra-small' }
        );
    };

    return (
        <SecondaryButton
            label="integrations.general.deactivate_integration_label"
            loading={deactivating}
            onClick={openConfirmationModal}
        />
    );
};

export default DeactivateIntegrationButton;
